import axios from 'axios';
import interceptor from '../common/interceptor';
import {
  extractResponseErrorMessage,
  isRefusedRequest,
} from '../common/interceptor/interceptor';
import { APIPaths } from '../common/constants';
import { ApiResponse } from '@/apis/types';
import {
  DayOfWeek,
  LBWorkflow,
  WorkflowTestRunData,
} from '@/proto/logiblocks/logiblocks';
import { NodeRunReport } from '@/proto/logiblocks/node_run_report';
import { VariableSource, VariableType } from '@/proto/common';
import {
  lbWorkflowToBase64,
  lbWorkflowFromBase64,
  protoFromBase64,
  testRunDataToBase64,
} from '@/pages/Logiblocks/serialization';

/**
 * TypeScript interfaces for Logiblocks API responses
 * These match the backend TypedDict definitions in logiblocks_view.py
 */

/** Aggregate run stats across all workflows for the company */
export interface WorkflowStats {
  total_runs: number;
  success_rate: number | null; // integer percentage, e.g. 87
  failures: number;
  avg_run_time: string | null; // e.g. "2m 30s"
  total_run_time: string | null; // e.g. "3d 4h"
  total_runs_change: number | null;
  success_rate_change: number | null;
  failures_change: number | null;
  avg_run_time_change: number | null;
  total_run_time_change: number | null;
  total_runs_is_negative: boolean;
  success_rate_is_negative: boolean;
  failures_is_negative: boolean;
  avg_run_time_is_negative: boolean;
  total_run_time_is_negative: boolean;
}

/** Workflow summary response (used in list view, without protobuf data) */
export interface WorkflowSummary {
  id: string;
  name: string | null;
  is_draft: boolean;
  graph_revision: number;
  last_run?: number | null;
  runs?: number;
  success_rate?: string | null;
  failures?: number;
  avg_run_time?: string | null;
  last_updated?: string | null;
  created_on?: string | null;
  created_by?: string | null;
}

/** Workflow detail response (used in get/create views, with protobuf data) */
export interface WorkflowDetail {
  id: string;
  name: string | null;
  is_draft: boolean;
  graph_revision: number;
  workflow: LBWorkflow;
}

export interface WorkflowGraphWriteResult {
  graph_revision: number;
  graph_fingerprint: string;
}

export class WorkflowGraphApiError extends Error {
  constructor(
    message: string,
    public readonly code?: string
  ) {
    super(message);
    this.name = 'WorkflowGraphApiError';
  }
}

/** Response from creating a draft workflow (includes new workflow and full list) */
export interface CreateDraftWorkflowResponse {
  new_workflow: WorkflowDetail;
  all_workflows: WorkflowSummary[];
}

/** Template summary response (used in list view, without protobuf data) */
export interface TemplateSummary {
  id: string;
  name: string;
  description: string;
}

/** Template detail response (with protobuf data) */
export interface TemplateDetail {
  id: string;
  workflow: LBWorkflow;
}

export type WorkflowRunStatus =
  | 'RUNNING'
  | 'SUSPENDED'
  | 'COMPLETED'
  | 'FAILED'
  // Stopped on purpose rather than by an error, e.g. the workflow was
  // unpublished while this run was still in progress.
  | 'CANCELED';

/** Where a run came from, returned verbatim from WorkflowRunHistory.Kind. */
export enum WorkflowRunKind {
  LIVE = 'LIVE',
  // Started from the editor. Its graph comes from getTestRunWorkflow.
  TEST = 'TEST',
}

// A node's outcome, returned verbatim from WorkflowRunNodeStatus.NodeStatus.
// (SUSPENDED is a workflow status; nodes don't report this status to the frontend.)
export type WorkflowRunNodeStatus =
  'RUNNING' | 'COMPLETED' | 'SKIPPED_CHILDREN' | 'FAILED';

export interface WorkflowRunOverview {
  total: number;
  in_progress: number;
  completed: number;
  failed: number;
  avg_runtime_ms: number | null;
}

/** A node that failed during a run, and why. */
export interface WorkflowRunFailureReason {
  node_id: string;
  /** Null if the node failed without recording a message. */
  error_message: string | null;
}

export interface WorkflowRunHistoryItem {
  /** Identifies the run to open. */
  id: string;
  /** Which run of this workflow this was, counting from 1. */
  run_number: number;
  /** A WorkflowRunHistory.Status value. */
  status: WorkflowRunStatus;
  /** UTC ISO-8601, e.g. "2026-04-24T11:00:00+00:00". */
  started_at: string;
  /** Same format. Null while the run is still going. */
  completed_at: string | null;
  /** How long the run took. Counts up while it is still going. */
  duration_ms: number;
  /**
   * The run's failed nodes, one entry per node. Not limited to failed runs: a
   * completed run carries entries for its failed parallel-loop iterations,
   * for example.
   */
  failure_reasons: WorkflowRunFailureReason[];
}

export interface WorkflowRunHistoryPagination {
  page: number;
  page_size: number;
  page_count: number;
  total_count: number;
}

export interface WorkflowRunHistoryList {
  results: WorkflowRunHistoryItem[];
  pagination: WorkflowRunHistoryPagination;
  overview: WorkflowRunOverview;
}

export interface WorkflowPublishEvent {
  /** Which published version this was, counting from 1. */
  version_number: number;
  /** UTC ISO-8601, e.g. "2026-04-24T11:00:00+00:00". */
  published_at: string;
}

/**
 * One attempt a node made, for the run's audit trail. A retried Call Webhook
 * node has one entry per try. `request_detail`/`response_detail` are shaped by
 * the type of the node the attempt belongs to. For Call Webhook they hold
 * `{method, url, header_names, content_type, body, body_bytes, body_truncated}`
 * and `{status_code, body}`. Header values are never stored.
 */
export interface WorkflowRunNodeAttempt {
  attempt_number: number;
  success: boolean;
  /** Name of the credential used, or "" when the node sent none. */
  credential_name: string;
  request_detail: Record<string, unknown>;
  response_detail: Record<string, unknown>;
  /** True when response_detail's body was cut for storage. */
  response_detail_truncated: boolean;
  error_message: string;
  duration_ms: number | null;
  /** UTC ISO-8601, e.g. "2026-04-24T11:00:00+00:00". */
  attempted_at: string;
}

export interface WorkflowRunNodeDetail {
  node_id: string;
  status: WorkflowRunNodeStatus;
  /** UTC ISO-8601, e.g. "2026-04-24T11:00:00+00:00". */
  started_at: string;
  /** Same format. Null while the node is still running. */
  completed_at: string | null;
  duration_ms: number;
  error_message: string | null;
  /** One entry per outbound attempt, oldest first. Empty for node types that make none. */
  attempts: WorkflowRunNodeAttempt[];
  /** What the node did during the run. Null if the node stored no report. */
  report: NodeRunReport | null;
}

// ---------------------------------------------------------------------------
// FilterControlFlow prefetched field types
// The API returns a flat FilterControlFlowField[], which
// getFilterControlFlowFields normalizes into { fields: FilterControlFlowField[] }.
// ---------------------------------------------------------------------------

/** A single option entry used by most filter fields (value/label shape). */
export interface FilterFieldOption {
  value: string;
  label: string;
}

/**
 * Courier option shape returned by the API for the "courier" filter field.
 * Matches what CourierSubFilterMenuBody already expects.
 */
export interface FilterCourierOption {
  id: string;
  name: string;
  service_levels?: Array<{
    id: string;
    service_type?: string;
    service_level?: string;
    display_name?: string;
    original_service_type?: string;
    original_service_level?: string;
  }>;
}

/**
 * A single field descriptor returned by the filterControlFlow prefetch API.
 * `options` can be:
 *  - FilterFieldOption[] for simple list fields (team, status, type, …)
 *  - FilterCourierOption[] for the "courier" field
 *  - AlertPhaseOption[] for the "alert_types" field
 *  - undefined for date-type fields (type === 6)
 * `pagination` is present on some large list fields but can be ignored —
 * the first-page results are sufficient for the node panel.
 */
export interface FilterControlFlowField {
  field_name: string;
  display_name: string;
  /** 0=STRING, 1=NUMBER, 5=ALERT_TYPE, 6=DATE_TIME */
  type: number;
  options?: Array<
    | FilterFieldOption
    | FilterCourierOption
    | AlertPhaseOption
    | Record<string, unknown>
  >;
  pagination?: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

export interface FieldOption {
  value: string;
  label: string;
}

/** Grouped option — one labelled category containing flat FieldOptions. */
export interface GroupedFieldOption {
  label: string;
  options: FieldOption[];
}

export interface AlertOption {
  value: string;
  label: string;
  description?: string;
  sensor_required?: string;
  is_critical_type?: boolean;
  is_enabled?: boolean;
}

export interface AlertPhaseOption {
  phase: string;
  phase_display: string;
  alerts: AlertOption[];
}

export interface FieldItem {
  field_name: string;
  /**
   * Name of the derived variable this item offers, as in the "status" of
   * shipment_id.status. Absent when the item isn't a derived variable.
   */
  derived_field_name?: string;
  /** Who declared the variable, stored in the VariableRef the picker builds. */
  source: VariableSource;
  display_name: string;
  /** VariableType numeric value (0=STRING, 1=NUMBER, 5=ALERT_TYPE, 6=DATE_TIME, …) */
  type: number;
  is_multi_select: boolean;
  /**
   * When true, this is a single-select enum field (fixed options).
   * Operators: Is, Is not, Exists, Does not exist (no Contains).
   */
  is_enum?: boolean;
  /** When true, options are grouped: [{ label: string, options: FieldOption[] }] */
  group?: boolean;
  example_string?: string;
  /** Flat FieldOption[], grouped GroupedFieldOption[], or AlertPhaseOption[] for ALERT_TYPE fields. */
  options?: FieldOption[] | GroupedFieldOption[] | AlertPhaseOption[];
}

export interface FieldGroup {
  group_name: string;
  color: string;
  name: string;
  /** The node that publishes these fields; saved as the selected variable's node id. */
  node_id: string;
  items: FieldItem[];
}

export interface FilterControlFlowFieldsPayload {
  /** When set, fetch just this field's paginated options. */
  paginated_field?: string;
  page?: number;
  search?: string;
}

export interface WorkflowRunHistoryDetail {
  /** Identifies the run to open. */
  id: string;
  /** Which run of this workflow this was, counting from 1. */
  run_number: number;
  kind: WorkflowRunKind;
  /** A WorkflowRunHistory.Status value. */
  status: WorkflowRunStatus;
  /** UTC ISO-8601, e.g. "2026-04-24T11:00:00+00:00". */
  started_at: string;
  /** Same format. Null while the run is still going. */
  completed_at: string | null;
  /** How long the run took. Counts up while it is still going. */
  duration_ms: number;
  /** Workflow node detail, in the order the run reached the nodes. */
  nodes: WorkflowRunNodeDetail[];
  /**
   * The published version of the workflow that produced this run. Null on runs
   * that predate version tracking, which leaves staleness unknowable.
   */
  workflow_version_number: number | null;
  /**
   * The workflow's newest published version. Differs from
   * workflow_version_number once the workflow has been republished since the
   * run, meaning the graph on screen is not the graph that ran.
   */
  current_version_number: number | null;
}

/**
 * Internal "raw" types used in backend communication, with protobuf data still
 * in serialized string form.. We don't expose these to the rest of the
 * frontend - instead, our external interfaces have a deserialized instance of
 * the protobuf data. (See e.g. WorkflowDetail, above)
 */
interface RawWorkflowDetail {
  id: string;
  name: string | null;
  is_draft: boolean;
  graph_revision: number;
  serialized_data: string;
}

/**
 * A WorkflowRunNodeDetail as the backend sends it, with `report` still a base64-encoded
 * NodeRunReport. `getWorkflowHistory` decodes it.
 */
type RawWorkflowRunNodeDetail = Omit<WorkflowRunNodeDetail, 'report'> & {
  report: string | null;
};

/**
 * A WorkflowRunHistoryDetail as the backend sends it, with each node's report still
 * base64-encoded. `getWorkflowHistory` decodes them.
 */
type RawWorkflowRunHistoryDetail = Omit<WorkflowRunHistoryDetail, 'nodes'> & {
  nodes: RawWorkflowRunNodeDetail[];
};

interface CreateWorkflowRequest {
  name?: string | null;
}

interface RawCreateDraftWorkflowResponse {
  new_workflow: RawWorkflowDetail;
  all_workflows: WorkflowSummary[];
}

interface RawTemplateDetail {
  id: string;
  serialized_data: string;
}

interface RawWorkflowVersionDetail {
  serialized_data: string;
}

interface RawStartTestRunResponse {
  run_id: string;
}

/** A shipment an alert trigger can replay in a test run, with the alert it replays. */
export interface TestShipmentCandidateRow {
  shipment_id: string;
  alert_id: string;
  /** The stored `ShipmentAlert.alert_type`, e.g. "low_battery". */
  alert_type: string;
  reference_number: string | null;
  link_code: string | null;
  /** The status as the shipment list shows it, e.g. "IN TRANSIT". */
  status: string | null;
  product_names: string[];
  /** UTC ISO-8601, e.g. "2026-04-24T11:00:00+00:00". */
  actual_pickup_date: string | null;
  /** Same format. */
  scheduled_pickup_date: string | null;
  /** Empty when the shipment has no courier. */
  courier_name: string;
  sensor_manufacturer_name: string | null;
  sensor_name: string | null;
  origin_name: string | null;
  destination_name: string | null;
}

interface RawTestShipmentCandidatesResponse {
  candidates: TestShipmentCandidateRow[];
}

/**
 * API Functions
 */

/**
 * List all workflows for the authenticated user's company
 * @param token - Authentication token
 * @returns Promise with array of workflow summaries (without protobuf data)
 */
export const listWorkflows = async (
  token: string,
  page?: number,
  search?: string,
  period?: string | null,
  startDate?: string | null,
  endDate?: string | null
): Promise<WorkflowSummary[]> => {
  const params = new URLSearchParams();

  if (page != null) params.append('page', page.toString());
  if (search) params.append('search', search);
  if (period) params.append('period', period);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);

  const query = params.toString();
  const url = query
    ? `${APIPaths.listWorkflows}?${query}`
    : APIPaths.listWorkflows;
  const response = (await interceptor.axiosGet(
    'Get',
    url,
    token
  )) as ApiResponse<WorkflowSummary[]>;

  return response?.data?.data?.data ?? [];
};

/**
 * Get a specific workflow by ID
 * @param token - Authentication token
 * @param workflowId - UUID of the workflow to retrieve
 * @returns Promise with workflow details
 * @throws Error if workflow not found or deserialization fails
 */
export const getWorkflow = async (
  token: string,
  workflowId: string
): Promise<WorkflowDetail> => {
  const url = `${APIPaths.getWorkflow}${workflowId}/`;
  const response = (await interceptor.axiosGet(
    'Get',
    url,
    token
  )) as ApiResponse<RawWorkflowDetail>;

  // Check if the request failed
  if (!response.data.data.is_success) {
    throw new Error(
      `Failed to get workflow: ${response.data.data.message || 'Unknown error'}`
    );
  }

  const backendData = response.data.data.data;
  if (!backendData) {
    throw new Error(
      `Failed to get workflow ${workflowId}: No data returned from server`
    );
  }

  const workflow = lbWorkflowFromBase64(backendData.serialized_data);
  return {
    id: backendData.id,
    name: backendData.name,
    is_draft: backendData.is_draft,
    graph_revision: backendData.graph_revision,
    workflow,
  };
};

/**
 * Create a new draft workflow (server creates empty LBGraph)
 * @param token - Authentication token
 * @param name - Name of the new workflow
 * @returns Promise with created workflow details and updated workflow list
 * @throws Error if request fails or deserialization fails
 */
export const createDraftWorkflow = async (
  token: string,
  name: string
): Promise<CreateDraftWorkflowResponse> => {
  const backendRequest: CreateWorkflowRequest = {
    name: name,
  };

  const response = (await interceptor.axiosPost(
    'Post',
    APIPaths.createDraftWorkflow,
    backendRequest,
    token
  )) as ApiResponse<RawCreateDraftWorkflowResponse>;

  // Check if the request failed
  if (!response.data.data.is_success) {
    throw new Error(
      `Failed to create draft workflow: ${response.data.data.message || 'Unknown error'}`
    );
  }

  const backendData = response.data.data.data;
  if (!backendData) {
    throw new Error(
      'Failed to create draft workflow: No data returned from server'
    );
  }

  const workflow = lbWorkflowFromBase64(
    backendData.new_workflow.serialized_data
  );
  return {
    new_workflow: {
      id: backendData.new_workflow.id,
      name: backendData.new_workflow.name,
      is_draft: backendData.new_workflow.is_draft,
      graph_revision: backendData.new_workflow.graph_revision,
      workflow,
    },
    all_workflows: backendData.all_workflows,
  };
};

/**
 * Duplicate an existing workflow as a new draft.
 * @param token - Authentication token
 * @param workflowId - ID of the workflow to duplicate
 * @returns Promise with the new draft's details and updated workflow list
 * @throws Error if request fails or deserialization fails
 */
export const duplicateWorkflow = async (
  token: string,
  workflowId: string
): Promise<CreateDraftWorkflowResponse> => {
  const url = `${APIPaths.duplicateWorkflow}${workflowId}/`;
  const response = (await interceptor.axiosPost(
    'Post',
    url,
    undefined,
    token
  )) as ApiResponse<RawCreateDraftWorkflowResponse>;

  // Check if the request failed
  if (!response.data.data.is_success) {
    throw new Error(
      `Failed to duplicate workflow: ${response.data.data.message || 'Unknown error'}`
    );
  }

  const backendData = response.data.data.data;
  if (!backendData) {
    throw new Error(
      'Failed to duplicate workflow: No data returned from server'
    );
  }

  const workflow = lbWorkflowFromBase64(
    backendData.new_workflow.serialized_data
  );
  return {
    new_workflow: {
      id: backendData.new_workflow.id,
      name: backendData.new_workflow.name,
      is_draft: backendData.new_workflow.is_draft,
      graph_revision: backendData.new_workflow.graph_revision,
      workflow,
    },
    all_workflows: backendData.all_workflows,
  };
};

/**
 * Update an existing workflow (draft only).
 *
 * Set `options.keepalive` to fire the request with fetch's keepalive flag so it
 * survives tab close / page unload. Keepalive is best-effort and capped at 64KB
 * body; use only for last-ditch unload flushes, not regular saves. Keepalive
 * calls bypass the global error handler (no toast / no auth-redirect on an
 * unmounting page) but the returned promise still rejects on transport or HTTP
 * failure — callers should attach `.catch()`.
 *
 * @throws Error on backend failure (non-keepalive path); rejects raw on the
 * keepalive path.
 */
export const updateWorkflow = async (
  token: string,
  workflowId: string,
  updates: {
    name?: string;
    workflow?: LBWorkflow;
    expectedGraphRevision?: number;
  },
  options?: { keepalive?: boolean }
): Promise<WorkflowGraphWriteResult> => {
  const backendRequest: {
    name?: string;
    serialized_data?: string;
    expected_graph_revision?: number;
  } = {};

  if (updates.name !== undefined) {
    backendRequest.name = updates.name;
  }

  if (updates.workflow !== undefined) {
    if (updates.expectedGraphRevision === undefined) {
      throw new Error('A graph revision is required when saving a workflow');
    }
    backendRequest.serialized_data = lbWorkflowToBase64(updates.workflow);
    backendRequest.expected_graph_revision = updates.expectedGraphRevision;
  }

  const url = `${APIPaths.updateWorkflow}${workflowId}/`;
  const fetchOptions = options?.keepalive ? { keepalive: true } : undefined;

  const response = (await interceptor.axiosPut(
    'Put',
    url,
    backendRequest,
    token,
    fetchOptions
  )) as ApiResponse<WorkflowGraphWriteResult>;

  // Check if the request failed
  if (!response.data.data.is_success) {
    const errorData = response.data.data.data as { code?: string } | undefined;
    throw new WorkflowGraphApiError(
      `Failed to update workflow: ${response.data.data.message || 'Unknown error'}`,
      errorData?.code
    );
  }
  const result = response.data.data.data;
  if (!result) {
    throw new Error('Failed to update workflow: No revision returned');
  }
  return result;
};

/**
 * Publish a draft workflow.
 * @param token - Authentication token
 * @param workflowId - ID of the workflow to publish
 * @param workflow - Optional workflow state to save before publishing
 * @throws Error if the request fails
 */
export const publishWorkflow = async (
  token: string,
  workflowId: string,
  expectedGraphRevision: number,
  workflow?: LBWorkflow
): Promise<WorkflowGraphWriteResult> => {
  const url = `${APIPaths.publishWorkflow}${workflowId}/`;

  const body = {
    expected_graph_revision: expectedGraphRevision,
    ...(workflow
      ? { serialized_data: lbWorkflowToBase64(workflow) }
      : undefined),
  };

  const response = (await interceptor.axiosPost(
    'Post',
    url,
    body,
    token
  )) as ApiResponse<WorkflowGraphWriteResult>;

  // Check if the request failed
  if (!response.data.data.is_success) {
    const errorData = response.data.data.data as { code?: string } | undefined;
    throw new WorkflowGraphApiError(
      `Failed to publish workflow: ${response.data.data.message || 'Unknown error'}`,
      errorData?.code
    );
  }
  const result = response.data.data.data;
  if (!result) {
    throw new Error('Failed to publish workflow: No revision returned');
  }
  return result;
};

/** What an unpublish attempt did, or what it needs before it will do it. */
export interface UnpublishWorkflowResult {
  /**
   * True when the workflow has runs in progress and the caller has not confirmed
   * yet. The workflow is still published, so call again with confirmCancelRuns.
   */
  needs_confirmation: boolean;
  /** Runs that were still going when the request arrived. */
  in_progress_run_count: number;
  /** Runs this call canceled. Zero when it only asked for confirmation. */
  canceled_run_count: number;
}

/**
 * Unpublish a published workflow, reverting it to draft state.
 *
 * Unpublishing cancels the workflow's runs that are in progress, so the backend
 * refuses until the caller confirms.
 *
 * @param token - Authentication token
 * @param workflowId - ID of the workflow to unpublish
 * @param confirmCancelRuns - Unpublish even if runs are in progress
 * @throws Error if the request fails
 */
export const unpublishWorkflow = async (
  token: string,
  workflowId: string,
  confirmCancelRuns = false
): Promise<UnpublishWorkflowResult> => {
  const url = `${APIPaths.unpublishWorkflow}${workflowId}/`;
  const body = confirmCancelRuns ? { confirm_cancel_runs: true } : undefined;
  const response = (await interceptor.axiosPost(
    'Post',
    url,
    body,
    token
  )) as ApiResponse<UnpublishWorkflowResult>;

  // Check if the request failed
  if (!response.data.data.is_success) {
    throw new Error(
      `Failed to unpublish workflow: ${response.data.data.message || 'Unknown error'}`
    );
  }

  const backendData = response.data.data.data;
  if (!backendData) {
    throw new Error('Failed to unpublish workflow: No data returned');
  }

  return backendData;
};

/**
 * Delete a draft workflow (soft delete).
 * @param token - Authentication token
 * @param workflowId - ID of the workflow to delete
 * @throws Error if the request fails (e.g., workflow is published or not found)
 */
export const deleteWorkflow = async (
  token: string,
  workflowId: string
): Promise<void> => {
  const url = `${APIPaths.deleteWorkflow}${workflowId}/`;
  const response = (await interceptor.axiosDelete(
    'Delete',
    url,
    undefined,
    token
  )) as ApiResponse<null>;

  // Check if the request failed
  if (!response.data.data.is_success) {
    throw new Error(
      `Failed to delete workflow: ${response.data.data.message || 'Unknown error'}`
    );
  }
};

/**
 * List all workflow templates
 * @param token - Authentication token
 * @returns Promise with array of template summaries
 */
export const listTemplates = async (
  token: string
): Promise<TemplateSummary[]> => {
  const response = (await interceptor.axiosGet(
    'Get',
    APIPaths.listTemplates,
    token
  )) as ApiResponse<TemplateSummary[]>;

  return response?.data?.data?.data ?? [];
};

/**
 * Get a specific template by ID
 * @param token - Authentication token
 * @param templateId - UUID of the template to retrieve
 * @returns Promise with template details (id and deserialized graph)
 * @throws Error if template not found or deserialization fails
 */
export const getTemplate = async (
  token: string,
  templateId: string
): Promise<TemplateDetail> => {
  const url = `${APIPaths.getTemplate}${templateId}/`;
  const response = (await interceptor.axiosGet(
    'Get',
    url,
    token
  )) as ApiResponse<RawTemplateDetail>;

  // Check if the request failed
  if (!response.data.data.is_success) {
    throw new Error(
      `Failed to get template: ${response.data.data.message || 'Unknown error'}`
    );
  }

  const backendData = response.data.data.data;
  if (!backendData) {
    throw new Error(
      `Failed to get template ${templateId}: No data returned from server`
    );
  }

  const workflow = lbWorkflowFromBase64(backendData.serialized_data);
  return {
    id: backendData.id,
    workflow,
  };
};

/**
 * A run graph (a published version or a test run's graph) that definitively
 * cannot be produced: it does not exist, or its bytes cannot be decoded.
 * Distinct from a transport error, which may succeed on retry.
 */
export class RunGraphUnavailableError extends Error {}

/**
 * Returns the decoded run graph at `url`. `label` describes the graph in error messages.
 * @throws RunGraphUnavailableError if the server refuses the request or deserialization fails;
 *   other request errors propagate as thrown
 */
async function getRunGraph(
  token: string,
  url: string,
  label: string
): Promise<LBWorkflow> {
  let response: ApiResponse<RawWorkflowVersionDetail>;
  try {
    response = (await interceptor.axiosGet(
      'Get',
      url,
      token
    )) as ApiResponse<RawWorkflowVersionDetail>;
  } catch (error) {
    // A graph that doesn't exist is an HTTP 4xx whose body holds the reason.
    if (axios.isAxiosError(error) && isRefusedRequest(error)) {
      const serverMessage =
        extractResponseErrorMessage(error.response?.data) ?? 'Unknown error';
      throw new RunGraphUnavailableError(
        `Failed to get ${label}: ${serverMessage}`
      );
    }
    throw error;
  }

  if (!response.data.data.is_success) {
    throw new RunGraphUnavailableError(
      `Failed to get ${label}: ${response.data.data.message || 'Unknown error'}`
    );
  }

  const backendData = response.data.data.data;
  if (!backendData) {
    throw new RunGraphUnavailableError(
      `Failed to get ${label}: No data returned from server`
    );
  }

  try {
    return lbWorkflowFromBase64(backendData.serialized_data);
  } catch (error) {
    throw new RunGraphUnavailableError(
      `Failed to decode ${label}: ${String(error)}`
    );
  }
}

/**
 * Get the graph of a workflow's published version.
 * @param token - Authentication token
 * @param workflowId - UUID of the workflow
 * @param versionNumber - The published version to retrieve
 * @returns Promise with the deserialized snapshot graph
 * @throws RunGraphUnavailableError if the server refuses the request or
 *   deserialization fails; transport errors propagate as thrown
 */
export const getWorkflowVersionSnapshot = (
  token: string,
  workflowId: string,
  versionNumber: number
): Promise<LBWorkflow> =>
  getRunGraph(
    token,
    `${APIPaths.getWorkflowVersion}${workflowId}/${versionNumber}/`,
    `workflow ${workflowId} version ${versionNumber}`
  );

/**
 * Get the graph a test run runs.
 * @param token - Authentication token
 * @param workflowId - UUID of the workflow
 * @param runId - The test run's WorkflowRunHistory id
 * @returns Promise with the deserialized test run graph
 * @throws RunGraphUnavailableError if the server refuses the request or
 *   deserialization fails; transport errors propagate as thrown
 */
export const getTestRunWorkflow = (
  token: string,
  workflowId: string,
  runId: string
): Promise<LBWorkflow> =>
  getRunGraph(
    token,
    `${APIPaths.getTestRunWorkflow}${workflowId}/${runId}/`,
    `workflow ${workflowId} test run ${runId}`
  );

/**
 * Start a test run of a workflow's graph.
 * @param token - Authentication token
 * @param workflowId - UUID of the workflow
 * @param testRunData - The graph and inputs to test with
 * @returns Promise with the test run's WorkflowRunHistory id
 * @throws Error with the server's message if the test run is refused
 */
export const startTestRun = async (
  token: string,
  workflowId: string,
  testRunData: WorkflowTestRunData
): Promise<string> => {
  const url = `${APIPaths.startTestRun}${workflowId}/`;
  const body = { serialized_data: testRunDataToBase64(testRunData) };
  let response: ApiResponse<RawStartTestRunResponse>;
  try {
    response = (await interceptor.axiosPost(
      'Post',
      url,
      body,
      token
    )) as ApiResponse<RawStartTestRunResponse>;
  } catch (error) {
    // A refused test run is an HTTP 400 whose body holds the refusal reasons.
    if (axios.isAxiosError(error)) {
      const serverMessage = extractResponseErrorMessage(error.response?.data);
      if (serverMessage !== undefined) {
        throw new Error(serverMessage);
      }
    }
    throw error;
  }

  if (!response.data.data.is_success) {
    throw new Error(response.data.data.message || 'Unknown error');
  }

  const result = response.data.data.data;
  if (!result) {
    throw new Error('Failed to start test run: No run id returned');
  }
  return result.run_id;
};

/**
 * List the shipments an alert trigger with the given alert types can replay in a test run.
 * @param token - Authentication token
 * @param alertTypes - The trigger's alert types; none means every type
 * @returns Promise with the candidates, newest first
 * @throws Error with the server's message if the request is refused
 */
export const getTestShipmentCandidates = async (
  token: string,
  alertTypes: string[]
): Promise<TestShipmentCandidateRow[]> => {
  const params = new URLSearchParams();
  for (const alertType of alertTypes) {
    params.append('alert_types', alertType);
  }
  const response = (await interceptor.axiosGet(
    'Get',
    `${APIPaths.getTestShipmentCandidates}?${params.toString()}`,
    token
  )) as ApiResponse<RawTestShipmentCandidatesResponse>;

  if (!response.data.data.is_success) {
    throw new Error(response.data.data.message || 'Unknown error');
  }

  const result = response.data.data.data;
  if (!result) {
    throw new Error('Failed to get test shipments: No candidates returned');
  }
  return result.candidates;
};

export const listWorkflowHistory = async (
  token: string,
  workflowId: string,
  options?: {
    page?: number;
    pageSize?: number;
    status?: WorkflowRunStatus | 'ALL';
  }
): Promise<WorkflowRunHistoryList> => {
  const params = new URLSearchParams({ workflow_id: workflowId });
  if (options?.page != null) {
    params.append('page', String(options.page));
  }
  if (options?.pageSize != null) {
    params.append('page_size', String(options.pageSize));
  }
  if (options?.status && options.status !== 'ALL') {
    params.append('status', options.status);
  }

  const response = (await interceptor.axiosGet(
    'Get',
    `${APIPaths.listWorkflowHistory}?${params.toString()}`,
    token
  )) as ApiResponse<WorkflowRunHistoryList>;

  if (!response.data.data.is_success) {
    throw new Error(
      `Failed to list workflow history: ${response.data.data.message || 'Unknown error'}`
    );
  }

  const backendData = response.data.data.data;
  if (!backendData) {
    throw new Error('Failed to list workflow history: No data returned');
  }

  return backendData;
};

export const getWorkflowPublishEvents = async (
  token: string,
  workflowId: string
): Promise<WorkflowPublishEvent[]> => {
  const params = new URLSearchParams({ workflow_id: workflowId });
  const response = (await interceptor.axiosGet(
    'Get',
    `${APIPaths.listWorkflowPublishEvents}?${params.toString()}`,
    token
  )) as ApiResponse<{ publish_events: WorkflowPublishEvent[] }>;

  if (!response.data.data.is_success) {
    throw new Error(
      `Failed to list workflow publish events: ${response.data.data.message || 'Unknown error'}`
    );
  }

  const backendData = response.data.data.data;
  if (!backendData) {
    throw new Error('Failed to list workflow publish events: No data returned');
  }

  return backendData.publish_events;
};

/**
 * Returns what one run of a workflow did. Set `options.silentErrors` to reject on a failed
 * request without the interceptor's error toast.
 */
export const getWorkflowHistory = async (
  token: string,
  workflowId: string,
  historyId: string,
  options?: { silentErrors?: boolean }
): Promise<WorkflowRunHistoryDetail> => {
  const params = new URLSearchParams({ workflow_id: workflowId });
  const response = (await interceptor.axiosGet(
    'Get',
    `${APIPaths.getWorkflowHistory}${historyId}/?${params.toString()}`,
    token,
    undefined,
    undefined,
    options?.silentErrors ?? false
  )) as ApiResponse<RawWorkflowRunHistoryDetail>;

  if (!response.data.data.is_success) {
    throw new Error(
      `Failed to get workflow history: ${response.data.data.message || 'Unknown error'}`
    );
  }

  const backendData = response.data.data.data;
  if (!backendData) {
    throw new Error('Failed to get workflow history: No data returned');
  }

  const nodes: WorkflowRunNodeDetail[] = [];
  for (const rawNode of backendData.nodes) {
    let report: NodeRunReport | null = null;
    if (rawNode.report !== null) {
      report = protoFromBase64(rawNode.report, NodeRunReport);
    }
    nodes.push({ ...rawNode, report });
  }
  return { ...backendData, nodes };
};

/**
 * Fetch aggregate run stats across all workflows for the company.
 */
export const getWorkflowStats = async (
  token: string,
  period?: string | null,
  startDate?: string | null,
  endDate?: string | null
): Promise<WorkflowStats> => {
  const params = new URLSearchParams();
  if (period) params.append('period', period);
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);

  const query = params.toString();
  const url = query
    ? `${APIPaths.workflowStats}?${query}`
    : APIPaths.workflowStats;
  const response = (await interceptor.axiosGet(
    'Get',
    url,
    token
  )) as ApiResponse<WorkflowStats>;

  return response.data.data.data;
};

/**
 * Response data structure for the filter-control-flow-fields API call.
 * Can return either a single field or a fields array depending on the request.
 */
interface NodeFieldsResponseData {
  field?: FieldItem;
  fields?: FieldItem | FieldGroup[] | FilterControlFlowField[];
}

/**
 * Fetch the FilterControlFlow node's filter fields and their dropdown options.
 * The backend returns { fields: [...] }; this unwraps and returns it directly.
 * When a paginated field is requested, the array holds just that field.
 */
export async function getFilterControlFlowFields(
  token: string,
  payload: FilterControlFlowFieldsPayload
): Promise<FieldItem> {
  const response = (await interceptor.axiosPost(
    'POST',
    `${APIPaths.filterControlFlowFields}`,
    payload,
    token
  )) as ApiResponse<NodeFieldsResponseData>;
  if (!response.data.data.is_success) {
    throw new Error(
      `Failed to load node fields: ${response.data.data.message || 'Unknown error'}`
    );
  }
  const data = response.data.data.data;
  if (!data) throw new Error('Failed to load node fields: No data returned');
  return data.field || (data.fields as FieldItem);
}

export interface GetVariableValueOptionsPayload {
  variable_name: string;
  /**
   * Name of the derived variable being referenced, as in the "status" of
   * shipment_id.status. Empty when the reference isn't to a derived variable.
   */
  derived_variable_name: string;
  /** Who declared the variable. */
  source: VariableSource;
  /** Base64-encoded LBNode of the node that publishes the variable. */
  serialized_data: string;
}

/** RHS value options for a conditional's selected variable — a subset of FieldItem. */
export interface VariableValueOptionsResult {
  type: VariableType;
  options?: FieldOption[] | GroupedFieldOption[] | AlertPhaseOption[];
}

/**
 * Fetch the allowed RHS values for a conditional's selected variable, narrowed
 * to what the variable's producing node can actually emit.
 */
export async function getVariableValueOptions(
  token: string,
  payload: GetVariableValueOptionsPayload
): Promise<VariableValueOptionsResult> {
  const response = (await interceptor.axiosPost(
    'Post',
    APIPaths.variableValueOptions,
    payload,
    token
  )) as ApiResponse<VariableValueOptionsResult>;
  if (!response.data.data.is_success) {
    throw new Error(
      `Failed to load variable value options: ${response.data.data.message || 'Unknown error'}`
    );
  }
  const data = response.data.data.data;
  if (!data) {
    throw new Error('Failed to load variable value options: No data returned');
  }
  return data;
}

export type {
  DropdownLabelItem,
  NodeDropdownLabelMap,
  WorkflowNodeDropdownLabels,
} from './logiblocksLabelsAPI';

// ---------------------------------------------------------------------------
// Date/day sets: system presets and saved date/day lists (ZOO-46)
// ---------------------------------------------------------------------------

/** Whether a preset or saved list holds calendar dates or days of the week
 * (the `kind` field of SavedDateListSerializer). */
export type DateSetKind = 'specific_dates' | 'days_of_week';

/** A day of the week by its proto enum name, as the saved-list API sends it
 * (SavedDateListSerializer `entries[].day`). */
export type DayOfWeekName = Exclude<keyof typeof DayOfWeek, 'UNRECOGNIZED'>;

/** One system preset row in the date-set value menu (SystemDatePresetResponse
 * in backend/logiblocks/view/list_system_date_presets_view.py). */
export interface SystemDatePresetSummary {
  preset_id: string;
  display_name: string;
  kind: DateSetKind;
  count: number;
  all_day: boolean;
  start_seconds: number | null;
  end_seconds: number | null;
  /** Zone the preset is always evaluated in; null when it follows the condition's. */
  iana_timezone: string | null;
}

/** Response of the list-system-date-presets endpoint
 * (ListSystemDatePresetsResponse in backend/logiblocks/view/list_system_date_presets_view.py). */
export interface ListSystemDatePresetsResult {
  presets: SystemDatePresetSummary[];
}

/** List the Zoomlogi-maintained system presets for the value menu. */
export const listSystemDatePresets = async (
  token: string
): Promise<ListSystemDatePresetsResult> => {
  const response = (await interceptor.axiosGet(
    'Get',
    APIPaths.listSystemDatePresets,
    token
  )) as ApiResponse<ListSystemDatePresetsResult>;

  if (!response.data.data.is_success) {
    throw new Error(
      `Failed to load system date presets: ${response.data.data.message || 'Unknown error'}`
    );
  }
  const data = response.data.data.data;
  if (!data) {
    throw new Error('Failed to load system date presets: No data returned');
  }
  return data;
};

/** A company alert tag: label and the action it is picked on
 * (AlertTagResponse in backend/logiblocks/view/list_alert_tags_view.py). */
export interface AlertTag {
  id: string;
  label: string;
  category: 'assignment' | 'resolution' | 'dismissal';
}

/** List the company's active alert tags, for the Alert action taken trigger's tag filter. */
export const listAlertTags = async (token: string): Promise<AlertTag[]> => {
  const response = (await interceptor.axiosGet(
    'Get',
    APIPaths.listAlertTags,
    token
  )) as ApiResponse<{ tags: AlertTag[] }>;

  if (!response.data.data.is_success) {
    throw new Error(
      `Failed to load alert tags: ${response.data.data.message || 'Unknown error'}`
    );
  }
  return response.data.data.data?.tags ?? [];
};

/** One entry's time-of-day filter, mirroring TimeOfDayRange in logiblocks.proto. */
export interface SavedDateListTimeRange {
  all_day: boolean;
  start_seconds?: number | null;
  end_seconds?: number | null;
}

/** A saved-list entry for a calendar date ('YYYY-MM-DD'). */
export interface SavedDateListDateEntry {
  date: string;
  time_range: SavedDateListTimeRange;
}

/** A saved-list entry for a day of the week. */
export interface SavedDateListDayEntry {
  day: DayOfWeekName;
  time_range: SavedDateListTimeRange;
}

/** A saved-list entry: a date for a 'specific_dates' list, a day for a
 * 'days_of_week' list. */
export type SavedDateListEntry = SavedDateListDateEntry | SavedDateListDayEntry;

/** A workflow that references a saved list (`used_in_workflows` of SavedDateListSerializer). */
export interface SavedDateListWorkflowRef {
  id: string;
  name: string | null;
}

interface SavedDateListFields {
  id: string;
  name: string;
  iana_timezone: string;
  entry_count: number;
  used_in_count: number;
  used_in_workflows: SavedDateListWorkflowRef[];
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

/** A saved date/day list as returned by SavedDateListSerializer
 * (backend/logiblocks/serializers/saved_date_list_serializer.py); `kind`
 * decides which entry shape `entries` holds. */
export type SavedDateList = SavedDateListFields &
  (
    | { kind: 'specific_dates'; entries: SavedDateListDateEntry[] }
    | { kind: 'days_of_week'; entries: SavedDateListDayEntry[] }
  );

/** The writable fields of a saved list, as accepted by the create and update
 * endpoints (SavedDateListSerializer). */
export type SavedDateListInput = Pick<SavedDateList, 'name' | 'iana_timezone'> &
  (
    | { kind: 'specific_dates'; entries: SavedDateListDateEntry[] }
    | { kind: 'days_of_week'; entries: SavedDateListDayEntry[] }
  );

/** Per-field errors from SavedDateListSerializer's validation. */
export type SavedDateListFieldErrors = Record<string, string[] | string>;

/** Raised when a saved-list API call fails validation — carries the
 * field-level error payload the serializer returned. */
export class SavedDateListValidationError extends Error {
  constructor(
    message: string,
    public readonly errors?: SavedDateListFieldErrors
  ) {
    super(message);
    this.name = 'SavedDateListValidationError';
  }
}

/** Raised when deleting a saved list is blocked because a workflow still
 * references it (the 409 of delete_saved_date_list_view.py) — carries the
 * usage detail for the caller to render without a second request. */
export class SavedDateListInUseError extends Error {
  constructor(
    message: string,
    public readonly usedInCount: number,
    public readonly usedInWorkflows: SavedDateListWorkflowRef[]
  ) {
    super(message);
    this.name = 'SavedDateListInUseError';
  }
}

/** The user-facing message for a failed saved-list call: the serializer's
 * `name` error when it has one, else the error's own message, else `fallback`. */
export function savedListErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof SavedDateListValidationError) {
    const nameError = e.errors?.name;
    if (Array.isArray(nameError)) return nameError[0] ?? e.message;
    return nameError ?? e.message;
  }
  return e instanceof Error ? e.message : fallback;
}

/** Envelope of a saved-list create/update: the list on success, the
 * serializer's field errors (or null) on failure. */
interface SavedDateListMutationResponse {
  data: {
    data:
      | { is_success: true; data: SavedDateList }
      | {
          is_success: false;
          message?: string;
          data: SavedDateListFieldErrors | null;
        };
  };
}

function throwSavedDateListError(
  message: string,
  errors: SavedDateListFieldErrors | null
): never {
  if (errors) throw new SavedDateListValidationError(message, errors);
  throw new Error(message);
}

/** List all saved date/day lists for the company, most-recently-used first. */
export const listSavedDateLists = async (
  token: string
): Promise<SavedDateList[]> => {
  const response = (await interceptor.axiosGet(
    'Get',
    APIPaths.listSavedDateLists,
    token
  )) as ApiResponse<SavedDateList[]>;

  if (!response.data.data.is_success) {
    throw new Error(
      `Failed to load saved date lists: ${response.data.data.message || 'Unknown error'}`
    );
  }
  return response.data.data.data ?? [];
};

/** Fetch a single saved date/day list, for the list-edit panel. */
export const getSavedDateList = async (
  token: string,
  listId: string
): Promise<SavedDateList> => {
  const url = `${APIPaths.getSavedDateList}${listId}/`;
  const response = (await interceptor.axiosGet(
    'Get',
    url,
    token
  )) as ApiResponse<SavedDateList>;

  if (!response.data.data.is_success) {
    throw new Error(
      `Failed to load saved date list: ${response.data.data.message || 'Unknown error'}`
    );
  }
  const data = response.data.data.data;
  if (!data) {
    throw new Error('Failed to load saved date list: No data returned');
  }
  return data;
};

/** Create a new saved date/day list ("Save as a list"). */
export const createSavedDateList = async (
  token: string,
  input: SavedDateListInput
): Promise<SavedDateList> => {
  const response = (await interceptor.axiosPost(
    'Post',
    APIPaths.createSavedDateList,
    input,
    token
  )) as SavedDateListMutationResponse;

  const body = response.data.data;
  if (body.is_success === false) {
    throwSavedDateListError(body.message || 'Failed to save list', body.data);
  }
  return body.data;
};

/** Update an existing saved date/day list (rename, adjust entries/timezone) —
 * the list-edit panel's "Apply" action. */
export const updateSavedDateList = async (
  token: string,
  listId: string,
  input: SavedDateListInput
): Promise<SavedDateList> => {
  const url = `${APIPaths.updateSavedDateList}${listId}/`;
  const response = (await interceptor.axiosPut(
    'Put',
    url,
    input,
    token
  )) as SavedDateListMutationResponse;

  const body = response.data.data;
  if (body.is_success === false) {
    throwSavedDateListError(body.message || 'Failed to update list', body.data);
  }
  return body.data;
};

/** Delete (soft-delete) a saved date/day list. Rejects with
 * SavedDateListInUseError if any workflow still references it. */
export const deleteSavedDateList = async (
  token: string,
  listId: string
): Promise<void> => {
  const url = `${APIPaths.deleteSavedDateList}${listId}/`;
  const response = (await interceptor.axiosDelete(
    'Delete',
    url,
    undefined,
    token
  )) as ApiResponse<SavedDateList | null>;

  if (!response.data.data.is_success) {
    const message = response.data.data.message || 'Failed to delete list';
    const usedIn = response.data.data.data;
    if (usedIn && usedIn.used_in_count > 0) {
      throw new SavedDateListInUseError(
        message,
        usedIn.used_in_count,
        usedIn.used_in_workflows
      );
    }
    throw new Error(message);
  }
};
export { getWorkflowNodeDropdownLabels } from './logiblocksLabelsAPI';
